import { inject, Injectable } from '@angular/core';
import { isNil, Nillable } from '@web/utils';
import { Observable } from 'rxjs';
import { CollectionFilterKey, RequiredMethodCallException } from '@web/common';
import { TableFilterType } from '../enums/table-filter-type';
import { BooleanTypeSelectItemsProviderService } from '../../boolean/providers/boolean-type-select-items-provider.service';
import { TableFilterConfig } from '../models/table-filter-config';
import { SelectItem } from '../../select-item/models/select-item';

@Injectable({
  providedIn: 'root',
})
export class TableFilterBuilder<Filter> {
  /* prettier-ignore */
  private readonly booleanTypeSelectItems = inject(BooleanTypeSelectItemsProviderService);

  private _filterable: Nillable<boolean>;
  private _key: Nillable<CollectionFilterKey<Filter>>;
  private _type: Nillable<TableFilterType>;
  private _options: Nillable<Observable<SelectItem<unknown>[]>>;

  public constructor() {
    this.reset();
  }

  public reset(): this {
    this._filterable = null;
    this._key = null;
    this._type = null;
    this._options = null;

    return this;
  }

  public initText(key: CollectionFilterKey<Filter>): this {
    return this.key(key).type(TableFilterType.Text);
  }

  public initCurrency(key: CollectionFilterKey<Filter>): this {
    return this.key(key).type(TableFilterType.Currency);
  }

  public initNumber(key: CollectionFilterKey<Filter>): this {
    return this.key(key).type(TableFilterType.Number);
  }

  public initDateRange(key: CollectionFilterKey<Filter>): this {
    return this.key(key).type(TableFilterType.DateRange);
  }

  public initDateTimeRange(key: CollectionFilterKey<Filter>): this {
    return this.key(key).type(TableFilterType.DateTimeRange);
  }

  public initBoolean(key: CollectionFilterKey<Filter>): this {
    return this.key(key)
      .type(TableFilterType.Boolean)
      .dictionary(() => this.booleanTypeSelectItems.selectItems());
  }

  public initSelect(key: CollectionFilterKey<Filter>): this {
    return this.key(key).type(TableFilterType.Select);
  }

  public initMultiSelect(key: CollectionFilterKey<Filter>): this {
    return this.key(key).type(TableFilterType.MultiSelect);
  }

  public dictionary<Dictionary>(
    factory: () => Observable<SelectItem<Dictionary>[]>,
  ): this {
    this._options = factory();

    return this;
  }

  public key(key: CollectionFilterKey<Filter>): this {
    this._key = key;

    return this;
  }

  public type(type: TableFilterType): this {
    this._type = type;

    return this;
  }

  public filterable(filterable: boolean): this {
    this._filterable = filterable;

    return this;
  }

  public build<Dictionary = never>(): TableFilterConfig<Filter, Dictionary> {
    this.requireKey();
    this.requireType();
    this.configureDefaultFilterable();

    if (this.shouldRequireDictionary()) {
      this.requireDictionary();
    }

    const config = new TableFilterConfig<Filter, Dictionary>(
      this._key as keyof Filter,
      this._filterable as boolean,
      this._type as TableFilterType,
      this._options as Nillable<Observable<SelectItem<Dictionary>[]>>,
    );

    this.reset();

    return config;
  }

  private shouldRequireDictionary(): boolean {
    return this.isSelect() || this.isMultiSelect();
  }

  private isSelect(): boolean {
    return this._type === TableFilterType.Select;
  }

  private isMultiSelect(): boolean {
    return this._type === TableFilterType.MultiSelect;
  }

  private requireKey(): void {
    if (!isNil(this._key)) {
      return;
    }

    throw new RequiredMethodCallException(this.key.name);
  }

  private requireType(): void {
    if (!isNil(this._type)) {
      return;
    }

    throw new RequiredMethodCallException(this.type.name);
  }

  private requireDictionary(): void {
    if (!isNil(this._options)) {
      return;
    }

    throw new RequiredMethodCallException(this.dictionary.name);
  }

  private configureDefaultFilterable(): void {
    if (!isNil(this._filterable)) {
      return;
    }

    this.filterable(true);
  }
}
